import React, { useEffect, useRef, useState } from "react";
import { haptic } from "../lib/haptics";
import { CoinHead, CoinTail } from "./CoinRupee";

/* ---------- Playing card (real pip layouts, court frames, monogram back) ---------- */
export function parseCard(c) {
  const s = String(c || "");
  const suit = s.slice(-1);
  const rank = s.slice(0, -1) || s;
  return { rank, suit, red: suit === "♥" || suit === "♦" };
}
const PIPS_LAYOUT = {
  2: [[1, 0], [1, 6]], 3: [[1, 0], [1, 3], [1, 6]], 4: [[0, 0], [2, 0], [0, 6], [2, 6]], 5: [[0, 0], [2, 0], [1, 3], [0, 6], [2, 6]],
  6: [[0, 0], [2, 0], [0, 3], [2, 3], [0, 6], [2, 6]], 7: [[0, 0], [2, 0], [1, 1.5], [0, 3], [2, 3], [0, 6], [2, 6]],
  8: [[0, 0], [2, 0], [1, 1.5], [0, 3], [2, 3], [1, 4.5], [0, 6], [2, 6]], 9: [[0, 0], [2, 0], [0, 2], [2, 2], [1, 3], [0, 4], [2, 4], [0, 6], [2, 6]],
  10: [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2], [0, 4], [2, 4], [1, 5], [0, 6], [2, 6]],
};
const COURT = { J: "JACK", Q: "QUEEN", K: "KING" };

export function CardBack({ className = "" }) {
  return <div className={`rl-card-face rl-card-back ${className}`}><span className="rl-monogram">RL</span></div>;
}

export function PlayingCard({ card, faceDown = false, delay = 0, size = "md", deal = true, highlight = false }) {
  const { rank, suit, red } = parseCard(card);
  const dims = { xs: "w-11 h-[62px] text-[12px]", sm: "w-[54px] h-[76px] text-sm", md: "w-[68px] h-[96px] text-base", lg: "w-24 h-[136px] text-xl" }[size];
  const [flipped, setFlipped] = useState(false);
  useEffect(() => {
    setFlipped(false);
    if (faceDown || !card) return undefined;
    const t = setTimeout(() => { setFlipped(true); haptic.flip(); }, delay + 350);
    return () => clearTimeout(t);
  }, [card, faceDown, delay]);
  const color = red ? "text-red-600" : "";
  return (
    <div className={`rl-card3d ${dims} ${deal ? "rl-deal-in" : ""} ${highlight ? "rl-card-glow" : ""}`} style={{ animationDelay: `${delay}ms` }} data-testid="playing-card">
      <div className={`rl-card3d-inner ${flipped ? "rl-flipped" : ""}`}>
        <CardBack />
        <div className={`rl-card-face rl-card-front ${color}`} style={{ color: red ? "#dc2626" : "#0f172a" }}>
          <span className="absolute top-[4%] left-[7%] font-black leading-none text-center" style={{ fontSize: "1em" }}>{rank}<br /><span style={{ fontSize: ".85em" }}>{suit}</span></span>
          <span className="absolute bottom-[4%] right-[7%] font-black leading-none text-center rotate-180" style={{ fontSize: "1em" }}>{rank}<br /><span style={{ fontSize: ".85em" }}>{suit}</span></span>
          <span className="absolute inset-0 flex flex-col items-center justify-center leading-none" data-testid="card-face">
            <span className="font-black" style={{ fontSize: "2.35em", letterSpacing: "-0.03em" }}>{rank}</span>
            <span style={{ fontSize: "1.6em", marginTop: "-0.05em" }}>{suit}</span>
            {COURT[rank] && <span className="uppercase font-bold tracking-widest opacity-70" style={{ fontSize: ".55em" }}>{COURT[rank]}</span>}
          </span>
        </div>
      </div>
    </div>
  );
}

export function DeckStack({ size = "sm", count = 4 }) {
  const dims = { sm: "w-10 h-14", md: "w-14 h-20", lg: "w-20 h-28" }[size];
  return (
    <div className={`relative ${dims}`} data-testid="deck-stack">
      {Array.from({ length: count }, (_, i) => <div key={i} className={`absolute inset-0 rounded-[0.6rem] ${dims}`} style={{ transform: `translate(${-i * 1.5}px, ${-i * 1.5}px)` }}><CardBack /></div>)}
    </div>
  );
}

/* ---------- Chip stack ---------- */
export function ChipStack({ amount, color = "#b91c1c" }) {
  return (
    <div className="rl-chipstack" data-testid="chip-stack">
      {[3, 2, 1, 0].map((i) => <span key={i} className="rl-chip" style={{ "--c": color, bottom: `${i * 4}px`, zIndex: 10 - i }}>{i === 0 && <b>₹{amount}</b>}</span>)}
    </div>
  );
}

/* ---------- Ivory 3D dice ---------- */
const PIPS = { 1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8] };
const FACE_ROT = { 1: "rotateX(0deg) rotateY(0deg)", 2: "rotateX(-90deg) rotateY(0deg)", 3: "rotateX(0deg) rotateY(-90deg)", 4: "rotateX(0deg) rotateY(90deg)", 5: "rotateX(90deg) rotateY(0deg)", 6: "rotateX(180deg) rotateY(0deg)" };
const Face = ({ v, cls }) => (
  <div className={`rl-die-face ${cls}`}>{Array.from({ length: 9 }, (_, i) => <span key={i} className="flex items-center justify-center">{PIPS[v].includes(i) && <span className={`rl-pip ${v === 1 ? "rl-pip-red" : ""}`} />}</span>)}</div>
);
export function Dice3D({ value, rolling, size = 64, delay = 0, readout = true }) {
  const [settled, setSettled] = useState(value);
  const wasRolling = useRef(false);
  useEffect(() => { if (!rolling) { setSettled(value); if (wasRolling.current) haptic.land(); } wasRolling.current = rolling; }, [value, rolling]);
  return (
    <div className="rl-die-scene" style={{ width: size, height: size + 14 + (readout ? 20 : 0), animationDelay: `${delay}ms` }}>
      <div className={`rl-die ${rolling ? "rl-die-rolling" : ""}`} style={{ "--s": `${size}px`, width: size, height: size, transform: rolling ? undefined : `rotateX(-22deg) rotateY(18deg) ${FACE_ROT[settled || 1]}`, animationDelay: `${delay}ms` }} data-testid="die">
        <Face v={1} cls="rl-f1" /><Face v={6} cls="rl-f6" /><Face v={3} cls="rl-f3" /><Face v={4} cls="rl-f4" /><Face v={2} cls="rl-f2" /><Face v={5} cls="rl-f5" />
      </div>
      <span className={`rl-die-shadow ${rolling ? "rl-die-shadow-roll" : ""}`} style={{ width: size * 0.9 }} />
      {readout && <span className={`rl-die-readout mt-1 ${rolling || !settled ? "opacity-0" : "rl-pop"}`} data-testid="die-readout">{settled || ""}</span>}
    </div>
  );
}

/* ---------- ₹5 coin (3D toss) ---------- */
export function Coin3D({ side, flipping }) {
  const finalDeg = side === "tail" ? 180 : 0;
  const was = useRef(false);
  useEffect(() => { if (was.current && !flipping) haptic.land(); was.current = flipping; }, [flipping]);
  return (
    <div className="rl-coin-scene" data-testid="coin">
      <div className={`rl-coin3d ${flipping ? "rl-coin-toss" : ""}`} style={{ transform: flipping ? undefined : `rotateY(${finalDeg}deg)` }}>
        <div className="rl-coin-face rl-coin-head"><CoinHead /></div>
        <div className="rl-coin-face rl-coin-tail"><CoinTail /></div>
        <div className="rl-coin-edge" />
      </div>
      <div className={`rl-coin-shadow ${flipping ? "rl-coin-shadow-toss" : ""}`} />
    </div>
  );
}

/* ---------- Scratch ticket (canvas foil) ---------- */
const SYMBOLS = ["7", "★", "♦", "♣", "♥", "♠", "♛", "☘"];
export function ScratchCard({ multiplier, stake, ticket = "", onRevealed, autoRevealAt = 0.6, labels = { win: "WIN", again: "TRY AGAIN", hint: "SCRATCH HERE", sub: "rub with your finger" } }) {
  const canvas = useRef(null);
  const [done, setDone] = useState(false);
  const [pct, setPct] = useState(0);
  const grid = React.useMemo(() => {
    const cells = [];
    for (let i = 0; i < 9; i++) cells.push(SYMBOLS[(i * 3 + 1) % SYMBOLS.length]);
    if (multiplier > 0) { cells[3] = "₹"; cells[4] = "₹"; cells[5] = "₹"; } else { cells[3] = "7"; cells[4] = "★"; cells[5] = "₹"; }
    return cells;
  }, [multiplier]);

  useEffect(() => {
    const cv = canvas.current; if (!cv) return undefined;
    const ctx = cv.getContext("2d");
    const w = cv.width, h = cv.height;
    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, "#b8bec9"); g.addColorStop(0.35, "#f1f5f9"); g.addColorStop(0.5, "#cbd5e1"); g.addColorStop(0.7, "#f8fafc"); g.addColorStop(1, "#94a3b8");
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "rgba(255,255,255,.35)"; ctx.lineWidth = 1;
    for (let i = -h; i < w; i += 14) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + h, h); ctx.stroke(); }
    ctx.fillStyle = "rgba(15,23,42,0.45)"; ctx.font = "900 20px Outfit, sans-serif"; ctx.textAlign = "center";
    ctx.fillText(labels.hint, w / 2, h / 2 - 4);
    ctx.font = "600 11px sans-serif"; ctx.fillText(labels.sub, w / 2, h / 2 + 14);
    let scratching = false, count = 0;
    const pos = (e) => { const r = cv.getBoundingClientRect(); const p = e.touches ? e.touches[0] : e; return [(p.clientX - r.left) * (w / r.width), (p.clientY - r.top) * (h / r.height)]; };
    const scratch = (e) => {
      if (!scratching) return; e.preventDefault();
      const [x, y] = pos(e);
      ctx.globalCompositeOperation = "destination-out"; ctx.fillStyle = "#000";
      ctx.beginPath(); ctx.arc(x, y, 20, 0, Math.PI * 2); ctx.fill();
      if (++count % 6 === 0) {
        const d = ctx.getImageData(0, 0, w, h).data; let clear = 0;
        for (let i = 3; i < d.length; i += 16) if (d[i] < 40) clear++;
        const p = clear / (d.length / 16); setPct(p);
        if (p >= autoRevealAt) { scratching = false; ctx.clearRect(0, 0, w, h); setDone(true); }
      }
    };
    const start = (e) => { scratching = true; scratch(e); };
    const end = () => { scratching = false; };
    cv.addEventListener("mousedown", start); cv.addEventListener("mousemove", scratch); window.addEventListener("mouseup", end);
    cv.addEventListener("touchstart", start, { passive: false }); cv.addEventListener("touchmove", scratch, { passive: false }); cv.addEventListener("touchend", end);
    return () => { cv.removeEventListener("mousedown", start); cv.removeEventListener("mousemove", scratch); window.removeEventListener("mouseup", end); cv.removeEventListener("touchstart", start); cv.removeEventListener("touchmove", scratch); cv.removeEventListener("touchend", end); };
  }, [autoRevealAt]); // eslint-disable-line
  useEffect(() => { if (done && onRevealed) onRevealed(); }, [done]); // eslint-disable-line

  return (
    <div className="rl-ticket" data-testid="scratch-card">
      <div className="rl-ticket-head">
        <div><div className="rl-ticket-brand">ROYAL LAKSHMI</div><div className="rl-ticket-title">SCRATCH &amp; WIN</div></div>
        <div className="text-right"><div className="rl-ticket-price">₹{stake}</div><div className="rl-ticket-no">No. {ticket || "RL-000000"}</div></div>
      </div>
      <div className="relative mx-2 rounded-lg overflow-hidden" style={{ height: 118 }}>
        <div className="absolute inset-0 p-1.5" style={{ background: "linear-gradient(135deg,#7c2d12,#b45309)" }}>
          <div className="grid grid-cols-3 gap-1 h-full">
            {grid.map((s, i) => <div key={i} className={`rounded-md flex items-center justify-center font-black text-xl ${i >= 3 && i <= 5 && multiplier > 0 ? "rl-ticket-win-cell" : "bg-[#fff7e6] text-[#7c2d12]"}`}>{s}</div>)}
          </div>
          {done && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className={`px-3 py-1.5 rounded-lg font-display font-black text-xl shadow-xl rl-pop ${multiplier > 0 ? "bg-emerald-500 text-white" : "bg-slate-900/90 text-white"}`}>{multiplier > 0 ? `${labels.win} ₹${(stake * multiplier).toFixed(0)} · ${multiplier}x` : labels.again}</span></div>}
        </div>
        {!done && <canvas ref={canvas} width={248} height={118} className="absolute inset-0 w-full h-full touch-none cursor-pointer" data-testid="scratch-canvas" />}
        {!done && pct > 0 && <span className="absolute top-1 right-1.5 text-[9px] font-bold text-slate-700 bg-white/80 px-1.5 rounded">{Math.round(pct * 100)}%</span>}
      </div>
      <div className="rl-ticket-foot"><span className="rl-barcode" /><span>MATCH 3 ₹ IN THE MIDDLE ROW · 18+</span></div>
    </div>
  );
}

/* ---------- Fortune wheel (bulbs, brass hub, jewel pointer) ---------- */
export function FortuneWheel({ segments, deg, spinning }) {
  const n = segments.length, step = 360 / n, R = 100;
  const color = (m) => (!m ? "#1e293b" : m >= 50 ? "#dc2626" : m >= 10 ? "#f59e0b" : m >= 3 ? "#7c3aed" : "#2563eb");
  const arc = (i) => {
    const a0 = ((i * step - 90) * Math.PI) / 180, a1 = (((i + 1) * step - 90) * Math.PI) / 180;
    return `M0 0 L${R * Math.cos(a0)} ${R * Math.sin(a0)} A${R} ${R} 0 0 1 ${R * Math.cos(a1)} ${R * Math.sin(a1)} Z`;
  };
  return (
    <div className="relative w-60 h-60" data-testid="fortune-wheel">
      <svg viewBox="-118 -118 236 236" className="w-full h-full">
        <defs><radialGradient id="fw-rim" cx="35%" cy="30%"><stop offset="0" stopColor="#fff3c4" /><stop offset=".6" stopColor="#d9a83a" /><stop offset="1" stopColor="#6b3f12" /></radialGradient></defs>
        <circle r={116} fill="url(#fw-rim)" />
        {Array.from({ length: 24 }, (_, i) => <circle key={i} cx={0} cy={-108} r={3.6} transform={`rotate(${i * 15})`} className={spinning ? (i % 2 ? "rl-bulb-a" : "rl-bulb-b") : ""} fill={i % 2 ? "#fff7cc" : "#ef4444"} />)}
        <g style={{ transform: `rotate(${deg}deg)`, transition: spinning ? "transform 3s cubic-bezier(0.12, 0.7, 0.1, 1)" : "none" }}>
          <circle r={101} fill="#fde68a" />
          {segments.map((m, i) => (
            <g key={i}>
              <path d={arc(i)} fill={color(m)} stroke="#fde68a" strokeWidth="1.2" />
              <text transform={`rotate(${i * step + step / 2}) translate(0,-74)`} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="900" fontFamily="Outfit">{m ? `${m}x` : "·"}</text>
            </g>
          ))}
          <circle r={22} fill="url(#fw-rim)" stroke="#5a3800" strokeWidth="2" />
          <text y="5" textAnchor="middle" fontSize="13" fontWeight="900" fill="#5a3800" fontFamily="'Cinzel',serif">RL</text>
        </g>
      </svg>
      <svg viewBox="0 0 30 34" className="absolute left-1/2 -top-1 -translate-x-1/2 w-8 h-9 drop-shadow-lg"><path d="M15 33 L2 6 Q15 -4 28 6 Z" fill="#f59e0b" stroke="#7c2d12" strokeWidth="2" /><circle cx="15" cy="9" r="4" fill="#dc2626" stroke="#fff" strokeWidth="1" /></svg>
    </div>
  );
}

/* ---------- Roulette wheel (wood rim, ball track, chrome turret) ---------- */
export const ROULETTE_ORDER = [0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26];
const RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
export function RouletteWheel({ deg, spinning, ballDeg, ballIn }) {
  const n = ROULETTE_ORDER.length, step = 360 / n, R = 92;
  const arc = (i) => {
    const a0 = ((i * step - 90) * Math.PI) / 180, a1 = (((i + 1) * step - 90) * Math.PI) / 180;
    return `M0 0 L${R * Math.cos(a0)} ${R * Math.sin(a0)} A${R} ${R} 0 0 1 ${R * Math.cos(a1)} ${R * Math.sin(a1)} Z`;
  };
  return (
    <div className="relative w-64 h-64" data-testid="roulette-wheel">
      <svg viewBox="-120 -120 240 240" className="w-full h-full">
        <defs>
          <radialGradient id="rw-wood" cx="40%" cy="35%"><stop offset="0" stopColor="#a5652b" /><stop offset=".7" stopColor="#5b2f10" /><stop offset="1" stopColor="#2a1408" /></radialGradient>
          <radialGradient id="rw-chrome" cx="35%" cy="30%"><stop offset="0" stopColor="#ffffff" /><stop offset=".5" stopColor="#cbd5e1" /><stop offset="1" stopColor="#475569" /></radialGradient>
        </defs>
        <circle r={119} fill="url(#rw-wood)" />
        <circle r={118} fill="none" stroke="#d9a83a" strokeWidth="1.5" />
        <circle r={104} fill="#1a0f08" />
        <circle r={104} fill="none" stroke="#d9a83a" strokeWidth="1" opacity=".7" />
        <g style={{ transform: `rotate(${deg}deg)`, transition: spinning ? "transform 3.2s cubic-bezier(0.15, 0.6, 0.1, 1)" : "none" }}>
          <circle r={94} fill="#d9a83a" />
          {ROULETTE_ORDER.map((num, i) => (
            <g key={num}>
              <path d={arc(i)} fill={num === 0 ? "#059669" : RED.has(num) ? "#b91c1c" : "#0f172a"} stroke="#d9a83a" strokeWidth="0.7" />
              <text transform={`rotate(${i * step + step / 2}) translate(0,-80)`} textAnchor="middle" fill="#fff" fontSize="8.5" fontWeight="800" fontFamily="Outfit">{num}</text>
            </g>
          ))}
          <circle r={62} fill="#14532d" stroke="#d9a83a" strokeWidth="2.5" />
          <circle r={56} fill="none" stroke="#0b3d1c" strokeWidth="4" />
          <circle r={20} fill="url(#rw-chrome)" />
          {[0, 90].map((a) => <rect key={a} x={-42} y={-3} width={84} height={6} rx={3} fill="url(#rw-chrome)" transform={`rotate(${a + 45})`} />)}
          <circle r={7} fill="#d9a83a" stroke="#5a3800" />
        </g>
      </svg>
      <div className="absolute inset-0 pointer-events-none" style={{ transform: `rotate(${ballDeg}deg)`, transition: spinning ? "transform 3.2s cubic-bezier(0.2, 0.8, 0.2, 1)" : "none" }}>
        <span className={`absolute left-1/2 w-3.5 h-3.5 -ml-[7px] rounded-full ${ballIn ? "top-[36px]" : "top-[10px]"}`} style={{ background: "radial-gradient(circle at 35% 30%, #fff, #cbd5e1 60%, #64748b)", boxShadow: "0 1px 4px rgba(0,0,0,.6)", transition: "top 0.6s ease-in 2.4s" }} />
      </div>
      <svg viewBox="0 0 20 18" className="absolute left-1/2 -top-1 -translate-x-1/2 w-5 h-4"><path d="M10 17 L1 2 L19 2 Z" fill="#f59e0b" stroke="#7c2d12" strokeWidth="1.5" /></svg>
    </div>
  );
}

/* ---------- Plinko board (chrome pegs, gold ball, prize pockets) ---------- */
export function PlinkoBoard({ mults, slot, dropping, onDone }) {
  const rows = mults.length - 1;
  const ball = useRef(null);
  const animRef = useRef(null);
  const W = 260, H = 300, top = 26, gapY = (H - 70) / rows, gapX = W / (mults.length + 1);
  useEffect(() => {
    if (!dropping || slot == null || !ball.current) return undefined;
    let rights = slot, lefts = rows - slot, x = W / 2;
    const frames = [{ transform: `translate(${x}px, ${top - 14}px)` }];
    for (let r = 0; r < rows; r++) {
      const goRight = rights > 0 && (lefts === 0 || Math.random() < 0.5);
      if (goRight) rights--; else lefts--;
      x += (goRight ? 1 : -1) * (gapX / 2);
      frames.push({ transform: `translate(${x}px, ${top + (r + 0.5) * gapY - 6}px)`, offset: (r + 0.5) / (rows + 1) });
      frames.push({ transform: `translate(${x}px, ${top + (r + 1) * gapY}px)`, offset: (r + 1) / (rows + 1) });
    }
    frames.push({ transform: `translate(${x}px, ${H - 26}px)` });
    animRef.current?.cancel();
    const anim = ball.current.animate(frames, { duration: 2600, easing: "linear", fill: "forwards" });
    animRef.current = anim;
    anim.onfinish = () => onDone && onDone();
    return undefined;
  }, [dropping, slot]); // eslint-disable-line
  return (
    <div className="relative rl-plinko-frame" style={{ width: W, height: H }} data-testid="plinko-board">
      <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full">
        <defs><radialGradient id="pk-peg" cx="35%" cy="30%"><stop offset="0" stopColor="#fff" /><stop offset=".6" stopColor="#cbd5e1" /><stop offset="1" stopColor="#334155" /></radialGradient></defs>
        <rect x={W / 2 - 14} y={0} width={28} height={top - 6} rx={4} fill="#1e293b" stroke="#d9a83a" strokeWidth="1" />
        {Array.from({ length: rows }, (_, r) => Array.from({ length: r + 2 }, (_, c) => {
          const cx = W / 2 + (c - (r + 1) / 2) * gapX, cy = top + (r + 1) * gapY;
          return <circle key={`${r}-${c}`} cx={cx} cy={cy} r={3.6} fill="url(#pk-peg)" />;
        }))}
      </svg>
      <div className="absolute inset-x-0 bottom-0 flex justify-center gap-[3px] px-1">
        {mults.map((m, i) => <span key={i} className={`flex-1 h-7 rounded-b-md rounded-t-sm text-[9px] font-black flex items-center justify-center transition-transform shadow-inner ${!dropping && i === slot ? "bg-amber-400 text-slate-900 scale-110 shadow-[0_0_12px_#fbbf24]" : m >= 10 ? "bg-red-600 text-white" : m >= 2 ? "bg-violet-600 text-white" : "bg-white/15 text-white/70"}`}>{m}x</span>)}
      </div>
      <span ref={ball} className="absolute top-0 left-0 w-4 h-4 -ml-2 rounded-full" style={{ transform: `translate(${W / 2}px, ${top - 14}px)`, opacity: dropping || slot != null ? 1 : 0.4, background: "radial-gradient(circle at 35% 30%, #fff7cc, #f59e0b 55%, #92400e)", boxShadow: "0 0 10px rgba(251,191,36,.9)" }} />
    </div>
  );
}

/* ---------- LUCKY 99 slot cabinet reels ---------- */
export function Reels({ value, spinning, digits = 2, title = "LUCKY 99" }) {
  const str = String(value ?? 0).padStart(digits, "0");
  return (
    <div className="rl-cabinet" data-testid="reels">
      <div className="rl-cabinet-title pt-1.5" data-testid="reels-title">{title}</div>
      <div className="flex gap-2 justify-center px-3 pb-3">
        {Array.from({ length: digits }, (_, i) => (
          <div key={i} className="rl-reel">
            <div className={`flex flex-col items-center ${spinning ? "rl-reel-spin" : ""}`} style={{ animationDelay: `${i * 0.15}s`, transform: spinning ? undefined : "translateY(0)" }}>
              {(spinning ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9] : [str[i]]).map((d, k) => <span key={k} className="h-20 flex items-center justify-center font-mono font-black text-4xl text-slate-900 shrink-0">{d}</span>)}
            </div>
            <div className="absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-black/30 to-transparent" /><div className="absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-black/30 to-transparent" />
            <div className="absolute inset-x-0 top-1/2 h-px bg-red-500/70" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Color ball drop machine ---------- */
export function BallDrop({ children, onDone }) {
  const cb = useRef(onDone); cb.current = onDone;
  useEffect(() => { const t = setTimeout(() => cb.current && cb.current(), 2200); return () => clearTimeout(t); }, []);
  return (
    <div className="relative h-36 w-full max-w-[280px] rounded-2xl overflow-hidden flex items-end justify-center rl-machine" data-testid="ball-drop">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-14 h-10 rounded-b-2xl bg-white/10 border border-white/25 border-t-0" />
      <div className="absolute bottom-0 inset-x-0 h-8 bg-black/40 border-t border-amber-300/40" />
      <div className="rl-ball-drop mb-3 scale-[1.6]">{children}</div>
    </div>
  );
}

/* ---------- Aviator sky canvas (plane sprite, grid, runway) ---------- */
function drawPlane(ctx, x, y, angle, crashed) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(-angle);
  ctx.fillStyle = crashed ? "#ef4444" : "#f87171";
  ctx.beginPath(); ctx.moveTo(18, 0); ctx.lineTo(-10, -5); ctx.lineTo(-14, 0); ctx.lineTo(-10, 5); ctx.closePath(); ctx.fill();
  ctx.fillStyle = crashed ? "#b91c1c" : "#fca5a5";
  ctx.beginPath(); ctx.moveTo(2, 0); ctx.lineTo(-6, -13); ctx.lineTo(-1, -13); ctx.lineTo(8, 0); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(2, 0); ctx.lineTo(-6, 13); ctx.lineTo(-1, 13); ctx.lineTo(8, 0); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(-10, 0); ctx.lineTo(-16, -7); ctx.lineTo(-13, 0); ctx.closePath(); ctx.fill();
  ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(10, -1, 1.6, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
}
export function AviatorCanvas({ mult, active, crashed }) {
  const ref = useRef(null);
  const pts = useRef([]);
  const stars = useRef(Array.from({ length: 40 }, () => [Math.random(), Math.random(), Math.random()]));
  useEffect(() => { if (active && mult <= 1.01) pts.current = []; }, [active, mult]);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext("2d"); const W = cv.width, H = cv.height;
    if (active) pts.current.push(mult);
    ctx.clearRect(0, 0, W, H);
    stars.current.forEach(([sx, sy, r]) => { ctx.fillStyle = `rgba(255,255,255,${0.25 + r * 0.5})`; ctx.beginPath(); ctx.arc(sx * W, sy * (H - 30), 0.6 + r, 0, Math.PI * 2); ctx.fill(); });
    ctx.strokeStyle = "rgba(255,255,255,0.07)"; ctx.lineWidth = 1;
    for (let i = 1; i < 5; i++) { ctx.beginPath(); ctx.moveTo(0, (H / 5) * i); ctx.lineTo(W, (H / 5) * i); ctx.stroke(); }
    for (let i = 1; i < 8; i++) { ctx.beginPath(); ctx.moveTo((W / 8) * i, 0); ctx.lineTo((W / 8) * i, H); ctx.stroke(); }
    ctx.fillStyle = "#1f2937"; ctx.fillRect(0, H - 18, W, 18);
    ctx.fillStyle = "#fbbf24"; for (let i = 0; i < W; i += 28) ctx.fillRect(i, H - 10, 14, 2);
    const p = pts.current; if (!p.length) { drawPlane(ctx, 30, H - 26, 0, false); return; }
    const maxM = Math.max(2, p[p.length - 1]);
    const X = (i) => 20 + (i / Math.max(p.length - 1, 40)) * (W - 60);
    const Y = (m) => H - 26 - ((m - 1) / (maxM - 1)) * (H - 70);
    ctx.beginPath(); ctx.moveTo(X(0), H - 18);
    p.forEach((m, i) => ctx.lineTo(X(i), Y(m)));
    ctx.lineTo(X(p.length - 1), H - 18); ctx.closePath();
    ctx.fillStyle = crashed ? "rgba(239,68,68,0.25)" : "rgba(251,191,36,0.22)"; ctx.fill();
    ctx.beginPath(); p.forEach((m, i) => (i ? ctx.lineTo(X(i), Y(m)) : ctx.moveTo(X(i), Y(m))));
    ctx.strokeStyle = crashed ? "#ef4444" : "#fbbf24"; ctx.lineWidth = 3; ctx.stroke();
    const lx = X(p.length - 1), ly = Y(p[p.length - 1]);
    const px = X(Math.max(0, p.length - 4)), py = Y(p[Math.max(0, p.length - 4)]);
    const ang = Math.atan2(py - ly, Math.max(1, lx - px));
    if (!crashed) drawPlane(ctx, lx + 8, ly - 6, ang, false);
    else { ctx.fillStyle = "rgba(239,68,68,.35)"; ctx.beginPath(); ctx.arc(lx, ly, 16, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = "#ef4444"; ctx.font = "900 12px Outfit"; ctx.textAlign = "center"; ctx.fillText("CRASH", lx, ly + 4); }
  }, [mult, active, crashed]);
  return <canvas ref={ref} width={360} height={176} className="absolute inset-0 w-full h-full" data-testid="aviator-canvas" />;
}
